import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Card,
  ConfirmDialog,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Field,
  Spinner,
} from '@wundo/ui';
import { addCohortParticipant, removeCohortParticipant } from '@/lib/cohorts.ts';
import { listUserTrackParticipants, userTrackParticipantsKey } from '@/lib/users.ts';
import { apiErrorMessage } from '@/lib/apiErrorMessage.ts';
import { CohortPicker } from '@/components/pickers/CohortPicker.tsx';

export interface UserCohortTabProps {
  userId: string;
  /** The user's own organisation: they can only join its cohorts. */
  organisationId: string | null;
}

/**
 * The one cohort this person belongs to, and the two ways that changes. A user
 * holds at most one enrolment, so "Add to cohort" is dead while there is one:
 * the way to a different cohort is out of this one first.
 */
type EnrolmentDialog = { kind: 'enrol' } | { kind: 'remove'; participantId: string };

export function UserCohortTab({ userId, organisationId }: UserCohortTabProps) {
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState<EnrolmentDialog | null>(null);
  const removingId = dialog?.kind === 'remove' ? dialog.participantId : null;

  const participantsKey = userTrackParticipantsKey(userId);

  const {
    data: participants,
    isPending,
    isError,
  } = useQuery({
    queryKey: participantsKey,
    queryFn: () => listUserTrackParticipants(userId),
  });

  const participant = participants?.[0] ?? null;
  const enrolledCohortIds = (participants ?? []).map((row) => row.cohort_id);

  const remove = useMutation({
    mutationFn: (participantId: string) => removeCohortParticipant(participantId),
    onSuccess: async () => {
      setDialog(null);
      await queryClient.invalidateQueries({ queryKey: participantsKey });
      await queryClient.invalidateQueries({ queryKey: ['cohorts', 'picker'] });
    },
  });

  if (isPending) {
    return (
      <Card className='flex justify-center'>
        <Spinner />
      </Card>
    );
  }

  if (isError) {
    return (
      <Card>
        <EmptyState
          title='Failed to load track participants'
          description='Try again in a moment.'
        />
      </Card>
    );
  }

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex flex-wrap items-center justify-between gap-3'>
        {participant ? (
          <p className='text-sm text-ink-muted'>
            User is in a cohort. Remove them first to put them in a different cohort.
          </p>
        ) : (
          <span />
        )}
        <div className='ml-auto flex items-center gap-2'>
          <Button
            size='sm'
            disabled={participant !== null}
            onClick={() => setDialog({ kind: 'enrol' })}
          >
            Add to cohort
          </Button>
          {participant && (
            <Button
              variant='dangerQuiet'
              size='sm'
              onClick={() => setDialog({ kind: 'remove', participantId: participant.id })}
            >
              Remove Enrollment
            </Button>
          )}
        </div>
      </div>

      {participant ? (
        <Card className='flex flex-col gap-2'>
          <Link
            to='/cohorts/$id'
            params={{ id: participant.cohort_id }}
            className='text-md font-semibold text-ink hover:underline'
          >
            {participant.track_class.title}
          </Link>
          <p className='text-sm text-ink-muted'>
            Track: {participant.track_class.track?.title || 'none'}
          </p>
        </Card>
      ) : (
        <Card>
          <EmptyState title='This user is not in any cohort.' />
        </Card>
      )}

      <EnrolInCohortDialog
        open={dialog?.kind === 'enrol'}
        onClose={() => setDialog(null)}
        userId={userId}
        organisationId={organisationId}
        enrolledCohortIds={enrolledCohortIds}
      />

      <ConfirmDialog
        open={removingId !== null}
        onOpenChange={(open) => !open && setDialog(null)}
        title='Remove track enrollment?'
        description='Are you sure you want to remove this track enrollment?'
        confirmLabel='Remove'
        cancelLabel='Cancel'
        variant='danger'
        onConfirm={async () => {
          if (removingId !== null) {
            await remove.mutateAsync(removingId).catch(() => undefined);
          }
        }}
      />
    </div>
  );
}

interface EnrolInCohortDialogProps {
  open: boolean;
  onClose: () => void;
  userId: string;
  /** The user's own organisation: they can only join its cohorts. */
  organisationId: string | null;
  /** Cohorts the user is already in, left out of the picker. */
  enrolledCohortIds: string[];
}

function EnrolInCohortDialog({
  open,
  onClose,
  userId,
  organisationId,
  enrolledCohortIds,
}: EnrolInCohortDialogProps) {
  const queryClient = useQueryClient();
  const [cohortId, setCohortId] = useState<string | undefined>(undefined);
  const [error, setError] = useState('');

  const enrol = useMutation({
    mutationFn: (cohort: string) => addCohortParticipant(cohort, userId),
    onSuccess: async () => {
      close();
      /* The picker's exclusions are read from the same list, so both refresh. */
      await queryClient.invalidateQueries({
        queryKey: userTrackParticipantsKey(userId),
      });
      await queryClient.invalidateQueries({ queryKey: ['cohorts', 'picker'] });
    },
    onError: (failure) => setError(apiErrorMessage(failure, 'Failed to enroll in track')),
  });

  function close() {
    setCohortId(undefined);
    setError('');
    onClose();
  }

  function submit() {
    if (cohortId === undefined) {
      setError('Please select a cohort');
      return;
    }
    setError('');
    enrol.mutate(cohortId);
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) close();
      }}
    >
      <DialogContent size='md'>
        <DialogHeader>
          <DialogTitle>Add to cohort</DialogTitle>
        </DialogHeader>

        <div className='flex flex-col gap-3.5 px-5 py-4'>
          {error !== '' && (
            <p
              role='alert'
              className='rounded-sm bg-danger-fill px-3.5 py-2.5 text-sm font-semibold text-danger'
            >
              {error}
            </p>
          )}

          <Field label='Select Cohort' required htmlFor='user-enrol-cohort'>
            <CohortPicker
              id='user-enrol-cohort'
              value={cohortId}
              onChange={setCohortId}
              organisationId={organisationId}
              excludeCohortIds={enrolledCohortIds}
              required
              disabled={enrol.isPending}
              placeholder='Choose a cohort...'
            />
          </Field>
        </div>

        <DialogFooter>
          <Button variant='quiet' disabled={enrol.isPending} onClick={close}>
            Cancel
          </Button>
          <Button
            variant='accent'
            disabled={enrol.isPending || cohortId === undefined}
            loading={enrol.isPending}
            onClick={submit}
          >
            Enroll
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
